import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import { useHasuraQuery } from "./useHasuraQuery";
import { useGraphQL } from "./useGraphQL";
import type {
  FileSearchFilters,
  FiltersState,
  FiltersAction,
  SpaceWithDisabled,
  Team,
} from "../types/assistant";

// GraphQL queries
const SPACES_CATEGORIES_QUERY = `
query SpacesCategoriesAndSpaces {
  paab_spaces_categories {
    id
    name
    spaces {
      id
      code
      title
    }
  }
}
`;

const RESTRICTED_SPACE_IDS_QUERY = `
query GetUserRestrictedSpaceIds($userId: String!) {
  getUserRestrictedSpaceIds(userId: $userId) {
    spaceIds
  }
}
`;

// Category IDs from paab-app
const BRAND_CATEGORY_ID = 139;
const COMPANY_CATEGORY_ID = 138;
const EXTERNAL_REVIEWERS_CATEGORY_ID = 67;

type SpacesCategory = {
  id: number;
  name: string;
  spaces: Array<{ id: number; code: string; title: string }>;
};

type SpacesCategoriesResponse = {
  paab_spaces_categories: SpacesCategory[];
};

type RestrictedSpaceIdsResponse = {
  getUserRestrictedSpaceIds: {
    spaceIds: number[];
  } | null;
};

const initialState: FiltersState = {
  source: "all",
  roles: [],
  teams: [],
  brands: [],
  company: null,
  externalReviewer: null,
};

const filterReducer = (
  state: FiltersState,
  action: FiltersAction
): FiltersState => {
  switch (action.type) {
    case "toggleRole":
      return {
        ...state,
        roles: state.roles.includes(action.role)
          ? state.roles.filter((r) => r !== action.role)
          : [...state.roles, action.role],
      };
    case "setSource":
      return {
        ...state,
        source: action.source,
        roles: action.source === "spaces" ? [] : state.roles,
      };
    case "toggleTeam":
      return {
        ...state,
        teams: state.teams.includes(action.team)
          ? state.teams.filter((t) => t !== action.team)
          : [...state.teams, action.team],
      };
    case "setBrands":
      return { ...state, brands: action.brands };
    case "setCompany":
      return { ...state, company: action.company };
    case "setExternalReviewer":
      return { ...state, externalReviewer: action.externalReviewer };
    case "clearAll":
      return initialState;
    default:
      return state;
  }
};

function extractTeamsFromCategories(categories: SpacesCategory[]): Team[] {
  const teamsMap = new Map<string, Team>();

  for (const category of categories) {
    const match = category.name.match(/^\(([^)]*)\)/);
    if (!match) continue;

    const teamName = match[1];
    const existing = teamsMap.get(teamName);
    if (existing) {
      existing.spaces.push(...category.spaces);
    } else {
      teamsMap.set(teamName, {
        name: teamName,
        spaces: [...category.spaces],
      });
    }
  }

  return Array.from(teamsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

export function useSpacesFilters() {
  const { user, isAuthenticated } = useAuthContext();
  const [filters, dispatch] = useReducer(filterReducer, initialState);

  // Fetch spaces categories from Hasura
  const {
    data: spacesData,
    error: spacesError,
    isLoading: spacesLoading,
    execute: executeSpacesQuery,
  } = useHasuraQuery<SpacesCategoriesResponse>();

  // Fetch restricted space IDs from the Dashboard API, as the signed-in person
  // (graphql-proxy). PAAB Hasura stopped serving this on 2026-10-07 (paab-hasura#14).
  const {
    data: restrictedData,
    errors: restrictedError,
    execute: executeRestrictedQuery,
  } = useGraphQL<RestrictedSpaceIdsResponse>();

  // Track if we've initiated a fetch
  const fetchInitiatedRef = useRef(false);

  // Fetch data once when authenticated
  useEffect(() => {
    if (isAuthenticated && !fetchInitiatedRef.current) {
      fetchInitiatedRef.current = true;
      executeSpacesQuery(SPACES_CATEGORIES_QUERY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Log errors
  useEffect(() => {
    if (spacesError) {
      console.error("useSpacesFilters spacesError:", spacesError);
    }
    if (restrictedError) {
      console.error("useSpacesFilters restrictedError:", restrictedError);
    }
  }, [spacesError, restrictedError]);

  useEffect(() => {
    if (user?.id) {
      executeRestrictedQuery(RESTRICTED_SPACE_IDS_QUERY, {
        userId: String(user.id),
      });
    }
  }, [user?.id, executeRestrictedQuery]);

  // Loading is true if we're actively loading spaces OR if we're authenticated but haven't loaded yet
  // If not authenticated, show empty state (not loading)
  const loading = spacesLoading || (isAuthenticated && spacesData === null && !spacesError);

  // Create set of excluded space IDs for O(1) lookup. Until the list arrives
  // (or if the lookup fails) we don't know which spaces are restricted, so every
  // space is disabled rather than none: an empty list means "no restrictions".
  const restrictedSpaceIds = restrictedData?.getUserRestrictedSpaceIds?.spaceIds;
  const restrictedSpacesKnown = !restrictedError && Array.isArray(restrictedSpaceIds);
  const excludedSpaceIdsSet = useMemo(
    () => new Set(restrictedSpaceIds ?? []),
    [restrictedSpaceIds]
  );

  // Memoize categories to avoid dependency issues
  const categories = useMemo(
    () => spacesData?.paab_spaces_categories ?? [],
    [spacesData]
  );

  // Helper to add disabled flag to spaces
  const addDisabledFlag = useCallback(
    (spaces: SpacesCategory["spaces"] | undefined): SpaceWithDisabled[] => {
      if (!spaces) return [];
      return spaces
        .map((space) => ({
          ...space,
          disabled: !restrictedSpacesKnown || excludedSpaceIdsSet.has(space.id),
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
    },
    [excludedSpaceIdsSet, restrictedSpacesKnown]
  );

  const teams = useMemo(() => extractTeamsFromCategories(categories), [categories]);

  const selectedTeams = useMemo(
    () => teams.filter((team) => filters.teams.includes(team.name)),
    [teams, filters.teams]
  );

  const brandSpaces = useMemo(() => {
    const category = categories.find((c) => c.id === BRAND_CATEGORY_ID);
    return addDisabledFlag(category?.spaces);
  }, [categories, addDisabledFlag]);

  const companySpaces = useMemo(() => {
    const category = categories.find((c) => c.id === COMPANY_CATEGORY_ID);
    return addDisabledFlag(category?.spaces);
  }, [categories, addDisabledFlag]);

  const externalReviewersSpaces = useMemo(() => {
    const category = categories.find((c) => c.id === EXTERNAL_REVIEWERS_CATEGORY_ID);
    return addDisabledFlag(category?.spaces);
  }, [categories, addDisabledFlag]);

  // Build fileSearchFilters for WebSocket payload
  const fileSearchFilters: FileSearchFilters = useMemo(() => {
    if (loading) return null;

    const hasNoFilters =
      filters.source === "all" &&
      filters.roles.length === 0 &&
      filters.teams.length === 0 &&
      filters.brands.length === 0 &&
      filters.company === null &&
      filters.externalReviewer === null;

    if (hasNoFilters) return null;

    const andFilters: Array<FileSearchFilters> = [];

    // Source filter
    if (filters.source !== "all") {
      andFilters.push({
        key: "source",
        type: "eq",
        value: filters.source,
      });
    }

    // Role filter (LMS only)
    if (filters.roles.length > 0 && filters.source !== "spaces") {
      // If source is "all", also pin to source=lms since role only exists on LMS files
      if (filters.source === "all") {
        andFilters.push({ key: "source", type: "eq", value: "lms" });
      }
      if (filters.roles.length === 1) {
        andFilters.push({ key: "role", type: "eq", value: filters.roles[0] });
      } else {
        andFilters.push({
          type: "or",
          filters: filters.roles.map((r) => ({ key: "role", type: "eq" as const, value: r })),
        });
      }
    }

    // Team filter (OR across all spaces in selected teams)
    if (selectedTeams.length > 0) {
      const allSpaces = selectedTeams.flatMap((team) => team.spaces);
      andFilters.push({
        type: "or",
        filters: allSpaces.map((space) => ({
          key: "spaceId",
          type: "eq" as const,
          value: space.id.toString(),
        })),
      });
    }

    // Brands filter (OR across selected brands)
    if (filters.brands.length > 0) {
      andFilters.push({
        type: "or",
        filters: filters.brands.map((brandId) => ({
          key: "spaceId",
          type: "eq" as const,
          value: brandId,
        })),
      });
    }

    // Company filter (single)
    if (filters.company) {
      andFilters.push({
        key: "spaceId",
        type: "eq",
        value: filters.company,
      });
    }

    // External reviewer filter (single)
    if (filters.externalReviewer) {
      andFilters.push({
        key: "spaceId",
        type: "eq",
        value: filters.externalReviewer,
      });
    }

    if (andFilters.length === 0) return null;
    if (andFilters.length === 1) return andFilters[0];

    return {
      type: "and",
      filters: andFilters.filter((f): f is NonNullable<typeof f> => f !== null),
    };
  }, [loading, filters, selectedTeams]);

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.source !== "all") count++;
    count += filters.roles.length;
    count += filters.teams.length;
    count += filters.brands.length;
    if (filters.company) count++;
    if (filters.externalReviewer) count++;
    return count;
  }, [filters]);

  return {
    filters,
    dispatch,
    teams,
    brandSpaces,
    companySpaces,
    externalReviewersSpaces,
    fileSearchFilters,
    loading,
    activeFilterCount,
    excludedSpaceIdsSet,
  };
}

export type UseSpacesFiltersReturn = ReturnType<typeof useSpacesFilters>;
